import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound,
  CheckCircle,
  Loader2,
  Calendar,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Card, Button, Input } from '@azyntor-labs/nexus-ui';
import apiClient from '@/api/client';
import { toast } from '@/utils/toast';
import { formatExamDateShort } from '@/utils';

export const EnrollWithCode = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [examDetails, setExamDetails] = useState<any>(null);

  // Validate code mutation
  const validateMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiClient.post('/enrollment-code/validate', { code });
      return response.data;
    },
    onSuccess: (data) => {
      setExamDetails(data.data);
      toast.success('Code is valid! Review exam details below.');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Invalid code');
      setExamDetails(null);
    },
  });

  // Enroll mutation
  const enrollMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiClient.post('/enrollment-code/enroll', { code });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Successfully enrolled in exam!');
      navigate('/student/dashboard');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to enroll');
    },
  });

  const handleValidate = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().length < 4) {
      toast.error('Please enter a valid code');
      return;
    }
    validateMutation.mutate(code.trim().toUpperCase());
  };

  const handleEnroll = () => {
    enrollMutation.mutate(code.trim().toUpperCase());
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/10 rounded-full mb-4">
            <KeyRound className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Enroll with Code</h1>
          <p className="text-gray-400">Enter your enrollment code to join an exam</p>
        </div>

        {/* Code Entry Form */}
        {!examDetails && (
          <Card className="bg-[#12121a]/80 border-white/[0.06] p-4 sm:p-8">
            <form onSubmit={handleValidate} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Enrollment Code
                </label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Enter code (e.g., CEH2025)"
                  className="text-center text-lg sm:text-2xl font-mono tracking-wider"
                  autoFocus
                  maxLength={20}
                />
                <p className="text-sm text-gray-500 mt-2">
                  Code is case-insensitive and usually 6-8 characters
                </p>
              </div>

              <Button
                type="submit"
                disabled={validateMutation.isPending || code.trim().length < 4}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                leftIcon={
                  validateMutation.isPending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )
                }
              >
                {validateMutation.isPending ? 'Validating...' : 'Validate Code'}
              </Button>
            </form>
          </Card>
        )}

        {/* Exam Details & Confirmation */}
        {examDetails && (
          <Card className="bg-[#12121a]/80 border-white/[0.06] p-4 sm:p-8">
            <div className="space-y-6">
              {/* Success Badge */}
              <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
                <div>
                  <p className="text-emerald-400 font-medium">Code Verified!</p>
                  <p className="text-sm text-gray-400">Review exam details and confirm enrollment</p>
                </div>
              </div>

              {/* Exam Details */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{examDetails.exam_title}</h2>
                {examDetails.exam_description && (
                  <p className="text-gray-400 mb-6">{examDetails.exam_description}</p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-[#0a0a0f] rounded-lg">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-xs text-gray-500">Start Date</p>
                      <p className="text-white font-medium">
                        {formatExamDateShort(examDetails.start_time)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-[#0a0a0f] rounded-lg">
                    <Clock className="w-5 h-5 text-teal-400" />
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="text-white font-medium">{examDetails.duration} minutes</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setExamDetails(null);
                    setCode('');
                  }}
                  variant="ghost"
                  className="flex-1 text-gray-400"
                  disabled={enrollMutation.isPending}
                >
                  Try Different Code
                </Button>
                <Button
                  onClick={handleEnroll}
                  disabled={enrollMutation.isPending}
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                  leftIcon={
                    enrollMutation.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ArrowRight className="w-5 h-5" />
                    )
                  }
                >
                  {enrollMutation.isPending ? 'Enrolling...' : 'Confirm Enrollment'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Don't have a code?{' '}
            <button
              onClick={() => navigate('/student/dashboard')}
              className="text-teal-400 hover:text-teal-300 underline"
            >
              Browse available exams
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
